/**
 * OSD Preservation Utilities
 *
 * Preserves OSD context during OAuth authentication flow
 * Stores OSD in localStorage before redirect, retrieves after callback
 *
 * Usage:
 * 1. Before OAuth: saveOSDBeforeAuth(currentOSD)
 * 2. After callback: const osd = getPreAuthOSD() || 'TAURON'
 * 3. Clean up: clearPreAuthOSD()
 */

import type { OSDCode } from '../osdMetadata';

const OSD_STORAGE_KEY = 'pre_auth_osd';

/**
 * Save OSD to localStorage before OAuth redirect
 *
 * @param osd - The OSD code to preserve during auth flow
 */
export function saveOSDBeforeAuth(osd: OSDCode): void {
  try {
    localStorage.setItem(OSD_STORAGE_KEY, osd);
  } catch (error) {
    // localStorage might be disabled in private/incognito mode
    console.error('Failed to save OSD before auth:', error);
  }
}

/**
 * Retrieve saved OSD after OAuth callback
 *
 * @returns The saved OSD code, or null if not found
 */
export function getPreAuthOSD(): OSDCode | null {
  try {
    const savedOSD = localStorage.getItem(OSD_STORAGE_KEY);
    return savedOSD as OSDCode | null;
  } catch (error) {
    console.error('Failed to retrieve pre-auth OSD:', error);
    return null;
  }
}

/**
 * Clear saved OSD after successful auth
 * Should be called after redirecting to dashboard
 */
export function clearPreAuthOSD(): void {
  try {
    localStorage.removeItem(OSD_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear pre-auth OSD:', error);
  }
}
